/**
 * Shared Event Constants
 *
 * Single source of truth for event names on both hops: Socket.io between web and
 * server, and the raw `ws` { event, data } envelope between server and runner.
 * Used by frontend, server, and agent.
 */

/**
 * All wire events used in the Bash system
 */
export const EVENTS = {
  // Agent Registration (agent → server)
  AGENT_REGISTER: 'agent:register',

  // Session Configuration (server → agent)
  SESSION_CONFIG: 'session:config',

  // User Messages (frontend → server → agent)
  USER_MESSAGE: 'user:message',
  // User message broadcast (server → other frontend tabs, excludes sender)
  USER_MESSAGE_BROADCAST: 'user_message:broadcast',

  // Agent Lifecycle (agent → server → frontend)
  AGENT_READY: 'agent:ready',  // Emitted when agent connects to server (before SDK init)
  AGENT_START: 'agent:start',
  AGENT_END: 'agent:end',
  /**
   * Authoritative turn-over signal (K3). Drives the UI `phase` flag for ALL arms.
   * AGENT_END remains the FINALIZATION carrier; this only flips done-ness.
   */
  SESSION_STATE_CHANGED: 'agent:session_state_changed',
  AGENT_ERROR: 'agent:error',
  AGENT_INTERRUPT: 'agent:interrupt',
  AGENT_USAGE: 'agent:usage', // Token usage stats per turn
  AGENT_OFFLOOP_USAGE: 'agent:offloop_usage', // Usage for an OFF-LOOP side call (memory sweep / next-prompt suggestion) — same payload as AGENT_USAGE plus `lane`. A separate name, not a flag on AGENT_USAGE, because these are NOT turns: they fire after the turn closed, so they must not drive turn analytics, tool-stat flushes or the live token counter. Deliberately absent from FORWARD_EVENTS — never reaches the frontend.
  AGENT_CONTEXT_USAGE: 'agent:context_usage', // Periodic context window snapshot (per turn)
  AGENT_MEMORY_RECALL: 'agent:memory_recall', // SDK 0.3.144: files the SDK surfaced to the model this turn
  AGENT_TOOL_BILLING: 'agent:tool_billing', // Dedicated tool billing event (all tools, parent + subagent)
  AGENT_BILLING_TRIPWIRE: 'agent:billing_tripwire', // Engine per-turn billing-keystone violation → server persists an error_event (never forwarded)
  AGENT_RECONSTRUCTION_MISMATCH: 'agent:reconstruction_mismatch', // Engine wire-vs-journal reconstruction desync (once per turn) → server persists an error_event (never forwarded)
  AGENT_HISTORY_HEALED: 'agent:history_healed', // Engine retried a model call on a safe view after the provider rejected the history (400 invalid_request_error) → server persists an error_event (never forwarded)
  AGENT_HOOK_STOP_CONTINUED: 'agent:hook_stop_continued', // A Stop hook's block kept the turn going (HOOK_PARITY_V1), so the engine billed another round → server persists an error_event (never forwarded)
  AGENT_STREAM_STALLED: 'agent:stream_stalled', // A native model stream went silent for the stall guard's whole window (re-issued or terminal) → server persists an error_event (never forwarded)
  AGENT_SPANS: 'agent:spans', // OTEL_TRACES_V1: the runner's finished trace spans for a turn whose USER_MESSAGE carried a sampled traceparent → the server records and exports them (the box never exports; never forwarded)

  // Observability: the sandbox ships its application logs to the server over the
  // existing reverse-WS; the server persists them to log_events. Push-on-write so
  // a turn's logs are query-ready WITHOUT exec'ing into / waking the sandbox.
  SANDBOX_LOG: 'sandbox:log',

  /**
   * The box reports a file under /app/workspace/files it just wrote (spec 2026-09-06,
   * train 2). It REPLACES the server's `sandbox.fs.watch` stream, which is an inbound
   * HTTP/2 stream and therefore billed the sandbox awake for as long as it stayed open —
   * and it never closed, because Blaxel's sandbox-api writes a keep-alive down it every
   * 30 s and the SDK's connection pool drops an idle session out of its map while the
   * stream still rides it. This frame rides the relay the runner already holds, so it
   * costs no connection at all. Server-consumed (registry `forward: false`): the relay
   * hands it to the same handleUserFileWatchEvent the watcher used to feed.
   */
  FILES_CHANGED: 'files:changed',

  // SDK System Events (agent → server → frontend)
  SYSTEM_INIT: 'system:init',
  AGENT_RESULT: 'agent:result',

  // Content Blocks (agent → server → frontend)
  AGENT_THINKING: 'agent:thinking',
  AGENT_THINKING_DELTA: 'agent:thinking_delta', // Streaming thinking chunks
  // Native-arm eager thinking pill. Fired the moment a thinking/reasoning block
  // OPENS (before any thinking_delta) so the frontend mounts the pill + its
  // running timer immediately — Anthropic's summarized adaptive thinking lags the
  // block start by seconds. This one MOUNTS a block.
  AGENT_THINKING_START: 'agent:thinking_start',
  AGENT_TEXT: 'agent:text',
  AGENT_TEXT_DELTA: 'agent:text_delta', // Streaming text chunks
  AGENT_TOOL_USE: 'agent:tool_use',
  AGENT_TOOL_RESULT: 'agent:tool_result',

  /**
   * Slice 2b: an in-code tool call dispatched by `execute_code` (Programmatic
   * Tool Calling). Emitted ADDITIVELY through the K3 single sink at the bridge
   * dispatch boundary (the exact PERMISSION_DENIED precedent — rides ctx.pushEvent,
   * forwarded verbatim). Pure observability: the server relay routes it to
   * `agentTrace.recordNestedToolCall(...)` ONLY — it is NEVER buffered into a
   * content block and produces NO UI card, preserving the single-emitter invariant.
   * Carries the nested call's own toolUseId + the parent execute_code toolUseId so
   * the trace links the nested `tool_calls` row to its parent.
   * Payload: { taskId, parentToolUseId, toolUseId, toolName, success, durationMs }.
   */
  AGENT_NESTED_TOOL_CALL: 'agent:nested_tool_call',

  // Phase 4 — Async Job Completion (server → frontend)
  // Fired when a previously queued provider job (e.g. fal video) becomes ready.
  // Frontend listens to swap a queued pill into the rendered media inline.
  // Payload: { jobId, request_id, messageId?, blockId?, result: UniversalResult }
  JOB_READY: 'job_ready',

  // Fired when a queued provider job fails — either at submit time (e.g. fal
  // returns 5xx and the server's intent.execute() throws) or asynchronously
  // via the provider's webhook with status: ERROR. Frontend swaps any visible
  // FalQueuedPill into a clean error variant.
  // Payload: { jobId, request_id, messageId?, blockId?, error: string }
  JOB_ERROR: 'job_error',

  // Fired while a queued media job is still generating (server → frontend).
  // Lets a visible queued pill show a live "generating" affordance ahead of
  // the terminal JOB_READY/JOB_ERROR.
  // Payload: { jobId, request_id, phase: 'generating' }
  JOB_PROGRESS: 'job_progress',

  // User Input (legacy — superseded by INTERACTION_REQUIRED/RESPONSE)
  AGENT_TODO: 'agent:todo',

  // ------------------------------------------------------------------
  // Interaction — unified user-input primitive (Input Station)
  //
  // Replaces (after Phase 4 cleanup):
  //   PENDING_QUESTION / QUESTION_ANSWER
  //   PENDING_APPROVAL / APPROVAL_RESPONSE
  //   PERMISSION_REQUEST / PERMISSION_RESPONSE
  //   CONNECTION_REQUIRED / CONNECTION_RESOLVED
  //   CREDIT_TOOL_CHECK / CREDIT_PERMISSION_RESPONSE
  //
  // See docs/superpowers/specs/2026-04-20-input-station.md
  // ------------------------------------------------------------------
  /** sandbox → server → frontend: a new pending interaction is live. Payload: InteractionRequiredPayload */
  INTERACTION_REQUIRED: 'interaction:required',
  /**
   * K5: server → sandbox (native/openai arms only). A resolved interaction's
   * continue trigger — the kernel materializes the bound tool_use IN PLACE and
   * continues the turn, instead of the Claude arm's [SYSTEM_RESUME] USER_MESSAGE.
   * Payload: ContinueInteractionPayload { taskId, interactionId, toolUseId, kind, outcome }.
   */
  CONTINUE_INTERACTION: 'interaction:continue',
  /**
   * P0.1: sandbox → server delivery receipt for a CONTINUE_INTERACTION. The sandbox
   * emits this the instant it receives the continue (BEFORE driving the resume), so the
   * server's push can confirm the continue landed on a LIVE socket instead of vanishing
   * into a half-open relay (the continue-delivery black hole). Server-internal only —
   * deliberately NOT in FORWARD_EVENTS (it never travels to the frontend).
   * Payload: { taskId, interactionId }.
   */
  CONTINUE_RECEIVED: 'continue:received',

  // SDK-Native Staging (message stream between user and SDK)
  MESSAGE_STAGED: 'message:staged',       // agent → server → frontend (message in staging area)
  MESSAGE_PICKED_UP: 'message:picked_up', // agent → server → frontend (SDK picked up message from staging)
  CANCEL_STAGED: 'message:cancel_staged', // frontend → server → agent (cancel staged message)

  // K6 P5 (D6/L5): full staged-queue snapshot for reconnect/recycle resync.
  // REQUEST: frontend → server → agent. RESPONSE: agent → server → frontend
  // (auto-forwarded via FORWARD_EVENTS). On agent-recycle the in-memory queue is
  // gone → snapshot.staged is empty → the FE clears all cards.
  STAGING_SYNC_REQUEST: 'staging:sync_request',
  STAGING_SYNC: 'staging:sync',

  // ==========================================================================
  // Permission control (K6-P3-C7)
  // ==========================================================================

  /**
   * K6-P3-C7: mid-session permission-mode control channel.
   * frontend → server → sandbox. A CONTROL event (NOT in FORWARD_EVENTS — it
   * never travels sandbox→frontend). The server authorizes (taskId ownership)
   * and forwards `{ taskId, mode }`; the sandbox validates the mode (C0
   * isPermissionMode) and flips the session's permission mode so the NEXT native
   * gate uses it. Bad mode → ignored (total). Native gate path only — the Claude
   * arm is mode-undefined, so this is a no-op there (byte-identical).
   * Payload: { taskId: string; mode: PermissionMode }.
   */
  SET_PERMISSION_MODE: 'permission:set_mode',

  /**
   * Permissions v2 (Phase A): mid-session ACCESS-LEVEL control channel — the sibling
   * of SET_PERMISSION_MODE for the human access dial. frontend → server → sandbox. A
   * CONTROL event (NOT in FORWARD_EVENTS — it never travels sandbox→frontend). The
   * server authorizes (taskId ownership), persists the per-task override
   * (tasks.access_level; null = clear/inherit), resolves the effective level
   * (task ?? user default ?? 'relaxed') and forwards `{ taskId, level }`. The sandbox
   * applies it SET-ALWAYS (a live picker change must win — unlike mode's set-if-absent
   * seed). Bad level → ignored. The wire value is the bare event name (fixed cross-arm
   * contract with the A1 sandbox receiver); its sibling above uses a namespaced value.
   * Payload: { taskId: string; level: AccessLevel }.
   */
  SET_ACCESS_LEVEL: 'SET_ACCESS_LEVEL',

  /**
   * Permissions v2 (Phase A3): the permissions-page grants channel — a request/reply
   * pair over the relay, correlated by requestId via awaitSandboxEvent, NEVER
   * forwarded to the frontend (server-internal). The sandbox owns the settings
   * files; the server proxies:
   *   LIST_REQUEST  { taskId, requestId }                    → sandbox reads the
   *   LIST_RESULT   { requestId, rules: [{ rule, source }] }    merged rules; the
   *      server enriches each with describeRule(label) before returning over HTTP.
   *   REMOVE_REQUEST { taskId, rule, source, requestId }     → sandbox deletes from
   *   REMOVE_RESULT  { requestId, ok }                          both dual-written
   *      files + invalidates its cache.
   * Cross-arm wire names — a fixed contract with the A4 sandbox handlers.
   */
  PERMISSION_RULES_LIST_REQUEST: 'permission:rules_list_request',
  PERMISSION_RULES_LIST_RESULT: 'permission:rules_list_result',
  PERMISSION_RULE_REMOVE_REQUEST: 'permission:rule_remove_request',
  PERMISSION_RULE_REMOVE_RESULT: 'permission:rule_remove_result',

  /**
   * Task history (harness to nine R3, TASK_FORK_V1): a request/reply pair over the relay,
   * correlated by requestId via awaitSandboxEvent, NEVER forwarded to the frontend:
   *   FORK_REQUEST { requestId, taskId (parent), childTaskId, anchorUuid } → the box copies the
   *   FORK_RESULT  { requestId, taskId, ok, entries?, parentSeq?, reason? }    parent's journal
   *      through that turn into the child's (schemas/task-history.schema.ts).
   */
  TASK_FORK_REQUEST: 'task:fork_request',
  TASK_FORK_RESULT: 'task:fork_result',
  /**
   * TASK_REWIND_V1 (R4b), the same kind of pair: REWIND_REQUEST { requestId, taskId, anchorUuid }
   * → REWIND_RESULT { requestId, taskId, ok, pivotSeq?, reason? }; the box rolls the task's journal
   * back to that turn's end.
   */
  TASK_REWIND_REQUEST: 'task:rewind_request',
  TASK_REWIND_RESULT: 'task:rewind_result',

  /** Server→sandbox: assert the user's durable server-truth permission rules for a session (B1). Payload: { taskId, rules: [{ rule, behavior }] }. Overwrites; empty array = revoke-all. */
  PERMISSION_RULES_SET: 'permission:rules_set',

  /** Server→sandbox: assert a session's effective custom AccessConfig (B1b), beside the rules assert at dispatch + redeliver. Payload: { taskId, config: AccessConfig | null }. Set-always; null CLEARS (revert to the preset access-level path). Also frontend→server on the same name for the per-task override control (persist + forward), mirroring SET_ACCESS_LEVEL. */
  PERMISSION_CONFIG_SET: 'permission:config_set',

  /**
   * K6-P3-C7: the gate refused a tool call. sandbox → server → frontend. Emitted ADDITIVELY
   * at the native deny surface (gateAndDispatchTool) and by the Claude Code engine's gate
   * listener (claude-code-harness.ts deniedEvent). The deny tool_result is unchanged. Carries
   * the denied tool, the gate's reason (written for the MODEL), a `reasonType` (security |
   * capability | rule | mode | floor | delivery) and, for an engine's own tool refused by name,
   * a person-facing `hint`. The relay composes the person's line onto it as `notice`.
   * Payload: { taskId, toolName, toolUseId, reason, reasonType?, hint?, notice? }.
   */
  PERMISSION_DENIED: 'permission:denied',

  /**
   * One quiet line for the person in a task (engines T0): a step the gate refused, or a
   * heads-up about their own plan. Sandbox → server → frontend when the runner authors it;
   * the relay buffers it so the turn persists it as a `notice` block.
   * Payload: { taskId, tone: 'quiet' | 'gold', text, ref }.
   */
  TASK_NOTICE: 'task:notice',

  // ==========================================================================
  // Task Management (Single ID: taskId === sdkSessionId)
  // ==========================================================================

  // Task created after SDK init (server → frontend)
  // Contains correlationId to match with pending message
  TASK_CREATED: 'task:created',

  // Resume existing task (frontend → server)
  TASK_RESUME: 'task:resume',

  // Task resumed successfully (server → frontend)
  TASK_RESUMED: 'task:resumed',

  // List all tasks for user
  TASK_LIST: 'task:list',

  // Task updated (status, title, etc.)
  TASK_UPDATE: 'task:update',

  /** Server → web: slim home-board delta (home-streams lane). Emitted to
   *  room user:{userId} on every board-relevant task write. */
  BOARD_TASK_DELTA: 'board:task_delta',

  /** Server → web: one task_story row (story spine). Push is the optimization;
   *  the task_story table is the truth. */
  STORY_ENTRY: 'story:entry',

  // Task deleted
  TASK_DESTROY: 'task:destroy',

  // Task title edited by user (frontend → server)
  TASK_TITLE_EDIT: 'task:title:edit',

  // Task output registered by agent (agent → server → frontend)
  TASK_OUTPUT_REGISTERED: 'task:output_registered',

  // One presented artifact landed (Task Outputs / Pieces; agent → server → frontend)
  TASK_ARTIFACT_REGISTERED: 'task:artifact_registered',

  // Live localhost preview lit for a task (server → frontend). Ephemeral — not a DB row.
  TASK_PREVIEW_READY: 'task:preview_ready',
  // Live localhost preview retracted (task ended / server stopped / sandbox slept).
  TASK_PREVIEW_CLOSED: 'task:preview_closed',

  // The user's app list changed (server → frontend). Payload: AppsChangedPayload (the FULL list).
  APPS_CHANGED: 'apps:changed',
  // Frontend → server: someone is looking at the app on this port. Payload: { port: number }.
  APPS_VIEW: 'apps:view',

  // Task resume failed (server → frontend)
  TASK_RESUME_FAILED: 'task:resume:failed',

  // Task stats query (frontend → server)
  TASK_STATS: 'task:stats',

  // Real-time task changes from Supabase (server → frontend)
  // Enables reactive dashboard updates without polling
  TASK_STREAM: 'task:stream',

  // ==========================================================================
  // Session Management (DEPRECATED - use Task events instead)
  // Kept for backwards compatibility during transition
  // ==========================================================================

  /** @deprecated Use TASK_CREATED with correlationId flow */
  SESSION_CREATE: 'session:create',
  /** @deprecated Use TASK_CREATED */
  SESSION_CREATED: 'session:created',
  /** @deprecated Use TASK_LIST */
  SESSION_LIST: 'session:list',
  /** @deprecated Use TASK_RESUME */
  SESSION_RESUME: 'session:resume',
  /** @deprecated Use TASK_DESTROY */
  SESSION_DESTROY: 'session:destroy',
  /** @deprecated Use TASK_UPDATE */
  SESSION_UPDATE: 'session:update',

  // Server -> Frontend: Full sandbox state with all agent processes
  SANDBOX_STATE: 'sandbox:state',

  // Connection Events (Socket.io built-in)
  CONNECT: 'connect',
  DISCONNECT: 'disconnect',
  CONNECTION_ERROR: 'connect_error',
  // Server -> Frontend (zero-gap deploys): the server is handing this tab to its successor. Sent to
  // the sending server's OWN tabs only, right before it disconnects them, so the browser reads the
  // disconnect that follows as a move: reconnect at once, show nothing.
  SERVER_HANDOVER: 'server:handover',

  // Internal Hook Events (agent-only, for debugging/monitoring)
  SECURITY_BLOCKED: 'security:blocked',
  AGENT_NOTIFICATION: 'agent:notification',
  // K6-P2 B4 — per-callback hook observability (native arm; gated default OFF).
  // The sandbox observer maps a kernel HookExecutionEvent → a `hook` HarnessEvent
  // and the core sink emits this. Agent-only telemetry; not forwarded by default.
  AGENT_HOOK_EXECUTION: 'agent:hook_execution',

  // ==========================================================================
  // Phase 11: State Management Cleanup - Server as Single Source of Truth
  // ==========================================================================

  // Message Sync (bidirectional)
  // Frontend requests sync on reconnect, server sends full conversation state
  MESSAGE_SYNC_REQUEST: 'message:sync_request',
  MESSAGE_SYNC: 'message:sync',

  // Task Sync Hint (server → frontend)
  // Server nudge to re-pull a task's messages from the DB after a relay flap
  // healed and a late (buffered) turn terminal was accepted post-reconcile. The
  // live client — whose own socket never blipped — treats the recovered AGENT_END
  // as a no-op, so this asks it to MESSAGE_SYNC_REQUEST and render the DB state.
  TASK_SYNC_HINT: 'task:sync_hint',

  // Injection Screen Notice (server → frontend, W4-2 shadow injection screen)
  // Emitted by the server's shadow screener when a side-effecting step in a
  // tainted turn is judged instructed by outside content rather than the user.
  // Informational + dismissible; nothing is blocked.
  INJECTION_SCREEN_NOTICE: 'agent:injection_screen_notice',

  // Message Complete (agent → server)
  // Sent when a complete message (user or assistant) should be persisted
  // Contains full message content for database storage
  MESSAGE_COMPLETE: 'message:complete',

  // ==========================================================================
  // Connection State (Unified Connection Service)
  // ==========================================================================

  // Presence Update (server → frontend) — Phase 1 connection-state redesign
  // Carries the `Presence` shape (see src/types/presence.ts). Replaces the
  // seven-value DisplayStatus emission path; `active`/`idle` are derived in
  // the UI from phase === 'ready' && activeTasks > 0, so there is no "idle"
  // waypoint between Starting and Active.
  PRESENCE_UPDATE: 'presence:update',

  // Turn-presence truth (runner → server), sent in reply to the server's WS
  // protocol ping. The protocol pong proves only that the socket echoes; this
  // frame carries WHICH turns the runner believes are open, so the server can
  // compare against its own turn ledger every beat (level-triggered — the
  // event stream stays the fast path, never the only path). Payload:
  // { activeTaskIds: string[], at: number }.
  RUNNER_STATUS: 'runner:status',

  // Turn reattach rail (spec 2026-08-31 §4). server → runner: "for these tasks I have up to
  // lastSeq; replay everything newer" (empty tasks = replay every pending task from 1).
  RELAY_RESUME: 'relay:resume',
  // runner → server: catch-up complete for these tasks (fromSeq..toSeq replayed; open = turn live).
  RELAY_RESUMED: 'relay:resumed',
  // server → runner: the terminal frame `seq` for taskId is banked — drop the turn's outbox.
  RELAY_ACK: 'relay:ack',
  // runner → server, before a replay whose oldest frames were dropped at the outbox cap.
  RELAY_GAP: 'relay:gap',

  // ==========================================================================
  // Sandbox Activity (Process-based Idle Detection)
  // ==========================================================================

  // Sandbox Idle (agent → server)
  // Emitted when ALL activity ends: no SDK query running, no tools executing, no subagents active
  // Server starts 25s idle timer on receipt; any activity event cancels the timer
  // This is the SOURCE OF TRUTH for idle state - sandbox knows its own activity
  SANDBOX_IDLE: 'sandbox:idle',

  // Sandbox Active (agent → server)
  // Emitted when activity resumes after being idle
  // Explicitly cancels any pending idle timer on server
  SANDBOX_ACTIVE: 'sandbox:active',

  // Sandbox State Change (agent → server)
  // Emitted on EVERY task transition (active/idle) with current activeTaskCount.
  // Sandbox is the source of truth for its own state. Server relays to frontend.
  SANDBOX_STATE_CHANGE: 'sandbox:state_change',

  // ==========================================================================
  // Integration Auth (agent ↔ server ↔ frontend)
  // ==========================================================================

  // Agent detected Composio tool auth failure — superseded by INTERACTION_REQUIRED (composio_auth kind)
  // Kept as a forwarding hint only; no server-side socket.on handlers remain as of Phase 9.
  CONNECTION_REQUIRED: 'connection:required',

  // Checkpoint: user clicked 'Keep going' → server resumes agent (no visible user message)
  CHECKPOINT_CONTINUE: 'checkpoint:continue',

  // ==========================================================================
  // State Robustness (Gaps 1-6)
  // ==========================================================================

  // Connection State Query (frontend → server)
  // Pull-based backup: frontend requests current sandbox state on connect/reconnect
  CONNECTION_STATE_QUERY: 'connection:state:query',

  // Connection heartbeat (frontend → server, acked). RTT evidence for the
  // client health rail: the server acks { serverTime } immediately, so a slow
  // or missed ack measures the transport itself — never sandbox state.
  CONNECTION_HEARTBEAT: 'connection:heartbeat',

  // Pre-warm sandbox on user typing (frontend → server)
  // Triggers background sandbox wake before user sends message.
  // Payload: { taskId?, agentSlug?, modelOverride? } — carries context so
  // the sandbox pre-spawns a WarmQuery for THIS specific task, not a
  // task-agnostic boot-time pre-warm (that was the 2026-04-20 bug class).
  SANDBOX_PRE_WARM: 'sandbox:pre_warm',

  // Typing-hold heartbeat (frontend → server, ~1Hz while composing).
  // Composing counts as working: the server forwards it to the sandbox as an
  // activity touch that DEFERS the post-task idle timer, so the sandbox stays
  // hot while the user types (answers "what if the timer ends while the user
  // is still typing"). Forwarded ONLY when the relay is live; waking a dead
  // sandbox stays the SANDBOX_PRE_WARM path's job. Empty payload — pure signal.
  SANDBOX_TYPING: 'sandbox:typing',

  // Pre-warm the SDK Query for a specific task (server → sandbox).
  // Sandbox calls SDK startup() with taskId-scoped options and parks a
  // WarmQuery. Consumed on the user's real USER_MESSAGE for this taskId.
  PREWARM_QUERY: 'sandbox:prewarm_query',

  // WarmQuery is ready (sandbox → server).
  // Emitted after SDK startup() resolves. Used internally for metrics and
  // for observability; the frontend indicator is driven by displayStatus,
  // not by this event (that layer was removed — displayStatus is the
  // single source of truth for "sandbox still coming up").
  WARMUP_QUERY_READY: 'sandbox:warmup_query_ready',

  // Message Gap Warning (server → frontend)
  // Emitted when reconciliation detects messages missing from database
  MESSAGE_GAP_WARNING: 'message:gap_warning',

  // ==========================================================================
  // Skill and agent ecosystem (server → frontend)
  // ==========================================================================

  // Agent Library Events
  AGENT_INSTALLED: 'agent:installed',
  AGENT_UNINSTALLED: 'agent:uninstalled',
  AGENT_PUBLISHED: 'agent:published',
  AGENT_UNPUBLISHED: 'agent:unpublished',

  // ==========================================================================
  // Agent Ecosystem (server → frontend)
  // ==========================================================================

  AGENT_CREATED: 'agent:created',
  AGENT_UPDATED: 'agent:updated',
  AGENT_DELETED: 'agent:deleted',

  // Subagent streaming events (agent → server → frontend)
  // Emitted by processor.ts when SDK spawns a subagent via Task tool
  SUBAGENT_START: 'subagent:start',
  SUBAGENT_TEXT_DELTA: 'subagent:text_delta',
  SUBAGENT_TOOL_USE: 'subagent:tool_use',
  SUBAGENT_TOOL_RESULT: 'subagent:tool_result',
  SUBAGENT_END: 'subagent:end',
  // P5.1 (Agent Teams) — a teammate task was spawned (parent Agent tool,
  // mode:'teammate') and a cross-lane message between a lead and a teammate.
  SUBAGENT_SPAWNED: 'subagent:spawned',
  SUBAGENT_MESSAGE: 'subagent:message',

  // Streaming Enrichment (SDK stream events surfaced to frontend)
  AGENT_TOOL_USE_START: 'agent:tool_use:start',
  AGENT_TOOL_PROGRESS: 'agent:tool_progress',
  // The fast lane's own narration, for the STORY rather than for the live
  // stream. AGENT_TOOL_PROGRESS is forwarded and forgotten (never buffered,
  // never minted), so a stretch left nothing behind: the box log line rotated
  // and a user watching the task saw nothing while the lane worked. This one is
  // NOT forwarded — the server turns it into a task_story row, and the row's own
  // story:entry push is what reaches the web.
  LANE_STORY: 'lane:story',
  AGENT_STATUS: 'agent:status',

  // Session observability hooks (Phase 2)
  // PostCompact: SDK finished auto-summarizing older turns → UI shows inline
  //   "context summarized" card with the summary text.
  // (SessionEnd / ExitReason intentionally skipped — per-turn observability
  //  is already covered by `terminal_reason` shipped in PR #54.)
  SESSION_COMPACTED: 'session:compacted',

  // Per-turn credit cost — fired when AGENT_RESULT's total_cost_usd arrives
  // (after MESSAGE_COMPLETE). Frontend merges creditCost into the message's
  // turn_footer block so MessageFooter's credits chip appears within ~1s
  // instead of waiting for a reload.
  MESSAGE_CREDIT_UPDATE: 'message:credit_update',

  // Elicitation events removed in Phase 9 — subsumed by request_user_input MCP tool
  // and the unified INTERACTION_REQUIRED / INTERACTION_RESPONSE pipeline.
  // No live socket.on handlers remain; the elicitation/ module in sandbox was deleted.

  // ==========================================================================
  // Workspace Sync (server → frontend, during wake)
  // ==========================================================================

  WORKSPACE_SYNC_STARTED: 'workspace:sync:started',
  WORKSPACE_SYNC_COMPLETED: 'workspace:sync:completed',

  // ==========================================================================
  // User files (agent outputs ↔ Supabase Storage)
  // ==========================================================================
  // Wire names migrated from `vault:*` to `files:*` during the Knowledge
  // pivot (Phase 4b). Web + server deploy together — no dual-emit phase
  // since we control all clients (no third-party socket consumers).

  // File synced to/from Storage (server → frontend)
  USER_FILE_SYNCED: 'files:file_synced',
  // File ready for inline display (real-time, during agent turn)
  USER_FILE_READY: 'files:file_ready',

  // ==========================================================================
  // The Line (composer live voice): server → browser only. Never relayed from
  // a sandbox, so neither event is in FORWARD_EVENTS (registry forward: false).
  // ==========================================================================
  COMPOSER_LIVE_STATE: 'composer:live:state',
  COMPOSER_LIVE_EVENT: 'composer:live:event',
  // The mute, browser -> server ONLY. The mute button takes the mic off the air
  // in the browser (track.enabled = false), which the vendor cannot tell from
  // silence, so the server's idle policy would ask "are you still there?" into a
  // dead microphone and close the line a grace later. The browser says which it
  // is, on every flip and once on open. Payload: LineMutePayload.
  COMPOSER_LINE_MUTE: 'composer:line:mute',

  // ==========================================================================
  // The app tells the server its screen (spec 2026-09-18, R-L1): browser →
  // server ONLY. The view the path is on, the task on screen and whether the
  // tab is visible; sent on a change (debounced), on visibilitychange, and on
  // every (re)connect, because the server keeps it on the SOCKET and it dies
  // with it. Read by the composer's rung chooser: a task that rests while its
  // person is looking at it says nothing. Never forwarded to a sandbox, never
  // emitted back down. Payload: ComposerScreenPayload.
  // ==========================================================================
  COMPOSER_SCREEN: 'composer:screen',

  // ==========================================================================
  // Credit System (server → frontend, frontend → server)
  // ==========================================================================

  // Balance changed after deduction (server → frontend)
  CREDIT_UPDATE: 'credit:update',
  // Low balance or grace period warning (server → frontend)
  CREDIT_WARNING: 'credit:warning',
  // Hard block — no more credits (server → frontend)
  CREDIT_EXHAUSTED: 'credit:exhausted',
  // User approves/denies high-cost tool (frontend → server)
  CREDIT_PERMISSION_RESPONSE: 'credit:permission_response',
  // Full credit summary pushed on socket connect (server → frontend)
  CREDIT_SUMMARY: 'credit:summary',
  // Realtime CREDIT_SUMMARY emit failed transiently — FE should refetch
  // via HTTP rather than show stale balance forever (server → frontend).
  CREDIT_SUMMARY_STALE: 'credit:summary_stale',
  // Balance sync (server → sandbox) — pushes balance + expensiveTools list
  CREDIT_SYNC: 'credit:sync',
  // Token refresh (server → sandbox) — pushes fresh auth token after reconnect
  TOKEN_REFRESH: 'token:refresh',

  // Ability reload (server -> sandbox). Wire value is unchanged from the
  // original SKILL_RELOAD constant so old sandboxes keep receiving it; the payload
  // widened in P4b to optionally carry the declared abilities head
  // (AbilityReloadPayload). Old handlers take no payload arg and ignore it.
  // The SKILL_RELOAD alias (same value) is gone: two keys on one wire value made
  // `satisfies Record<EventName, …>` one row weaker than it reads, since EventName
  // is a union of VALUES. This map is bijective; a test pins it.
  ABILITY_RELOAD: 'skill:reload',

  // Runner drain (server → sandbox) — Bedrock Slice 5 (Phase 6). Asks a LOCAL
  // runner to rotate for a self-update: finish the in-flight turn, record the
  // workspace head, release the lease, and self-exit with RUNNER_DRAIN_EXIT_CODE
  // so its CLI supervisor restarts it on the declared version. The server emits
  // this INSTEAD of killing (a laptop process it cannot reach) when the lease
  // environment is 'local'. A Blaxel runner never receives it.
  RUNNER_DRAIN: 'runner:drain',

  // Runner draining (sandbox → server) — the 09:04Z dark-window fix. A runner about to
  // self-drain for a self-update (fingerprint-converge → exit 75) emits this on its live
  // server connection just before exiting. Nothing auto-restarts a Blaxel runner, so the
  // server marks a short "respawn expected" window: the relay reconnect loop + wakeV2 stay
  // quiet (no CONNECT_LOST flash, no re-mint storm) while the old process finishes exiting,
  // then normal escalation spawns the successor. Best-effort — a dropped notice just falls
  // back to the level-triggered escalation ladder (the stamp is a hint, never a gate).
  RUNNER_DRAINING: 'runner:draining',

  // Move doorbell (server → sandbox) — Bedrock Slice 6 (Phase 1). After a Blaxel→local
  // MOVE fences + kills the Blaxel holder, the server rings this at the PRE-DIALED local
  // runner so it acquires the freed lease PROMPTLY (rather than waiting for its next
  // dial-retry poll). Best-effort — a dropped doorbell just falls back to the runner's
  // own 4409 fast-retry (R-B); it is never a correctness dependency. A Blaxel runner
  // that stays put never receives it.
  MOVE_DOORBELL: 'runner:move_doorbell',

  // Idle release (server → runner), box hosts Plan 2c (spec §5). A runner that dials out from a
  // cloud machine (boat) keeps its own dial socket open, and an open socket holds the runner, so its
  // idle exit never ran and the machine stayed on. When the server agrees the box is idle (the same
  // work, viewer, apps and sound guards a Blaxel box gets) it sends this instead of closing the
  // socket: the socket stays open for new work but no longer holds the runner, whose idle exit then
  // releases the lease as 'idle', and the server stops the machine. Only a dial-out cloud runner acts
  // on it (apps/sandbox runner/idle-release.ts); a Blaxel runner never receives it.
  RUNNER_IDLE_RELEASE: 'runner:idle_release',

  // Workspace materialized (sandbox → server) — Bedrock Slice 6 (Phase 1). A
  // workspace-head-capable runner has materialized the recorded head for its lease
  // epoch (the acquire-side materialize returned 'proceed'), so it is now safe to
  // redeliver its queued inbox. The server HOLDS redelivery for a capable runner until
  // this ack lands for the current epoch — making materialize-before-drain structural
  // rather than a boot-timing race (never-fork, §4.7). A refuse never emits this.
  WORKSPACE_MATERIALIZED: 'runner:workspace_materialized',

  // Workspace-head mismatch (sandbox → server) — Bedrock Slice 6 (Phase 2, D6). A move's
  // successor REFUSED: it could not materialize the recorded head (unfetchable, or extracted
  // but did not verify), so it released the lease and self-terminated rather than serve a fork.
  // The server runs onMoveRefused: a one-turn sticky-to-source override for the next placement
  // decision + a loud placement-refusal trace + ensure the source stays re-acquirable. Placement
  // is never advanced by a refusal. Carries {epoch, environment (the refusing target),
  // sourceEnvironment (the head's origin), reason}. Absent → an older runner only logs locally.
  WORKSPACE_HEAD_MISMATCH: 'runner:workspace_head_mismatch',

  // Save now (box hosts, Plan 4, 4-R6): server → runner, record the workspace head under the
  // runner's hold right away; runner → server, the one answer, echoing the requestId
  // (awaitSandboxEvent). The move job asks before a switch when the newest head is stale or
  // missing. Payloads: RunnerRecordHeadRequest / RunnerRecordHeadResult. Never forwarded.
  RUNNER_RECORD_HEAD: 'runner:record_head',
  RUNNER_RECORD_HEAD_RESULT: 'runner:record_head_result',

  // Tool blocked by credit hook in sandbox (sandbox → server → frontend)
  CREDIT_BLOCKED: 'credit:blocked',

  // Pre-execution credit gate (expensive tools only)
  // Sandbox → Server: tool is in expensiveTools set, needs cost resolution + approval
  CREDIT_TOOL_CHECK: 'credit:tool_check',
  // Server → Sandbox: approval verdict after user responds (or auto-deny if insufficient)
  CREDIT_TOOL_VERDICT: 'credit:tool_verdict',
  // Server → Frontend: inline approval block for expensive tool (replaces modal)
  CREDIT_APPROVAL_NEEDED: 'credit:approval_needed',

  // ==========================================================================
  // Intelligent Ingest — Phase 3 (ENABLE_INTELLIGENT_INGEST gated)
  // ==========================================================================

  /**
   * Server → frontend: a user message was classified as a deposit and durably
   * captured (DB persist + volume sync). No assistant turn will follow — the
   * frontend must NOT render an optimistic loader on receipt of this event.
   * Only emitted when ENABLE_INTELLIGENT_INGEST=true. Payload: DepositAckPayload.
   */
  DEPOSIT_ACK: 'deposit_ack',

  // ==========================================================================
  // Support Chat (Customer Widget + Admin Dashboard)
  // ==========================================================================

  // Customer → Server
  SUPPORT_CONVERSATION_OPEN: 'support:conversation:open',
  SUPPORT_MESSAGE_SEND: 'support:message:send',
  SUPPORT_TYPING_START: 'support:typing:start',
  SUPPORT_TYPING_STOP: 'support:typing:stop',
  SUPPORT_CONVERSATION_LIST: 'support:conversation:list',
  SUPPORT_ARTICLE_SEARCH: 'support:article:search',

  // Server → Customer
  SUPPORT_MESSAGE_RECEIVED: 'support:message:received',
  SUPPORT_CONVERSATION_UPDATE: 'support:conversation:update',

  // Admin → Server
  SUPPORT_REPLY_SEND: 'support:reply:send',
  SUPPORT_NOTE_SEND: 'support:note:send',
  SUPPORT_ASSIGN: 'support:assign',
  SUPPORT_RESOLVE: 'support:resolve',
  SUPPORT_PRIORITY_UPDATE: 'support:priority:update',
  SUPPORT_INBOX_SUBSCRIBE: 'support:inbox:subscribe',

  // Server → Admin
  SUPPORT_NEW_CONVERSATION: 'support:new_conversation',
  SUPPORT_MESSAGE_NEW: 'support:message:new',
  SUPPORT_AGENT_ASSIGNED: 'support:agent:assigned',

  // Customer → Server (resolve own ticket)
  SUPPORT_CUSTOMER_RESOLVE: 'support:customer:resolve',

  // Server → Customer (read receipts)
  SUPPORT_MESSAGES_READ: 'support:messages:read',

  // Customer → Server (concierge round: evidence + rating)
  SUPPORT_RECENT_TASKS: 'support:recent_tasks',
  SUPPORT_ATTACHMENT_SIGN: 'support:attachment:sign',
  SUPPORT_RATE: 'support:rate',
  SUPPORT_LINK_TASK: 'support:link_task',

  // ==========================================================================
  // Routines (Automated Task Execution)
  // ==========================================================================

  ROUTINE_CREATED: 'routine:created',
  ROUTINE_UPDATED: 'routine:updated',
  ROUTINE_DELETED: 'routine:deleted',
  ROUTINE_RUN_STARTED: 'routine:run:started',
  ROUTINE_RUN_COMPLETED: 'routine:run:completed',
  ROUTINE_RUN_FAILED: 'routine:run:failed',
  ROUTINE_REGISTER_FAILED: 'routine:register_failed',
  ROUTINE_CREATED_IN_TASK: 'routine:created_in_task',
  ROUTINE_COMPLETED: 'routine:completed',

  // Item (skill/agent/ability) created inside a task (sandbox → server → frontend).
  // Mirrors ROUTINE_CREATED_IN_TASK: emitted after a creation detection fires;
  // frontend renders the item confirmation "success card" in the task stream.
  ITEM_CREATED_IN_TASK: 'item:created_in_task',

  // Prompt suggestion (agent → server → frontend)
  PROMPT_SUGGESTION: 'prompt:suggestion',

  // P5 Task 3 — in-loop write-on-surprise capture signal (agent → server).
  // SERVER-CONSUMED (routed to the curator + trigger fabric); never forwarded
  // to the frontend — deliberately NOT in FORWARD_EVENTS.
  NAMI_CAPTURE_SIGNAL: 'nami:capture_signal',

  // Nami P1 first-hand memory — deposit carrying a memory from the sandbox to
  // the server (sandbox → server). SERVER-CONSUMED; never forwarded to the
  // frontend — deliberately NOT in FORWARD_EVENTS. Mirrors NAMI_CAPTURE_SIGNAL.
  NAMI_REMEMBER_DEPOSIT: 'nami:remember_deposit',

  // Agents first (train 1c): the box mirrors an agent's notebook file to the server at
  // a turn boundary (sandbox to server). SERVER-CONSUMED; never forwarded to the
  // frontend, deliberately NOT in FORWARD_EVENTS. Mirrors NAMI_REMEMBER_DEPOSIT.
  AGENT_NOTEBOOK: 'agent:notebook',

  // Nami Keeper PR1 — the end-of-capture signal (sandbox → server). The hot
  // memory pass is INVISIBLE by construction: its turn emits no AGENT_END and no
  // MESSAGE_COMPLETE (turnActive was never re-armed, so emitAgentEnd would no-op),
  // which left the server unable to know a sweep happened, let alone that its
  // deposits had stopped arriving. Every pre-existing trigger hangs off
  // MESSAGE_COMPLETE — the FIRST frame of the user turn's terminal, a whole model
  // turn BEFORE any deposit exists. This is the only frame ordered strictly after
  // the deposits, and it carries `deposited` so the fold can wait for them.
  //
  // `deposited` is POST-cap (parseMemorySweep stops at 30) and POST-dedup: it is
  // the count of frames actually emitted, NOT the count of FACT lines the model
  // produced. That is precisely what a barrier wants. Emitted on BOTH terminal
  // arms — the sweep's own terminal, and a turn that never staged a pass
  // (deposited:0), because six guards mean most tasks never sweep and without the
  // zero arm they would never fold.
  //
  // SERVER-CONSUMED; never forwarded to the frontend — deliberately NOT in
  // FORWARD_EVENTS. Mirrors NAMI_CAPTURE_SIGNAL / NAMI_REMEMBER_DEPOSIT.
  // Payload: { taskId: string; deposited: number }.
  NAMI_SWEEP_COMPLETE: 'nami:sweep_complete',

  // A3-T2 Reach v1 — the ONE out-of-band rail (server → frontend). Emitted by
  // notifyUser() into the user's room (emitToUser), never by a sandbox, so it
  // is deliberately NOT in FORWARD_EVENTS: forwarding is the sandbox→frontend
  // path, and this event has no sandbox leg to forward. One frame per
  // nami_notifications row that clears the gate; the row is the durable copy
  // and this is the live nudge. Payload: NamiNotifyPayload.
  NAMI_NOTIFY: 'nami:notify',


  // ==========================================================================
  // Admin Dashboard (admin ↔ server)
  // ==========================================================================

  // Admin subscribes to real-time health updates (admin → server)
  ADMIN_HEALTH_SUBSCRIBE: 'admin:health:subscribe',
  // Error event broadcast to admin room (server → admin)
  ADMIN_ERROR_EVENT: 'admin:error:event',
  // Admin message delivered to user (server → user)
  ADMIN_MESSAGE_RECEIVED: 'admin:message:received',

  // ==========================================================================
  // Admin Issue Tracker (admin ↔ server)
  // ==========================================================================

  // Issue created (server → admin room)
  ADMIN_ISSUE_CREATED: 'admin:issue:created',
  // Issue updated (server → admin room)
  ADMIN_ISSUE_UPDATED: 'admin:issue:updated',
  // Issue deleted (server → admin room)
  ADMIN_ISSUE_DELETED: 'admin:issue:deleted',
  // Comment added to issue (server → admin room)
  ADMIN_ISSUE_COMMENT_ADDED: 'admin:issue:comment:added',
  // Comment updated (server → admin room)
  ADMIN_ISSUE_COMMENT_UPDATED: 'admin:issue:comment:updated',
  // Comment deleted (server → admin room)
  ADMIN_ISSUE_COMMENT_DELETED: 'admin:issue:comment:deleted',

  // ==========================================================================
  // Capability flags (server → frontend, emitted once per connection after auth).
  // Currently only carries { hasBrowserStreaming: boolean } — the wire-level
  // companion to the BROWSER_MODE env var on the sandbox side. Kept across the
  // browser-streaming reset so the new (Steel-based) browser implementation can
  // pick up the same plumbing without re-adding the event.
  USER_CAPABILITIES: 'user:capabilities',

  // ==========================================================================
  // Browser streaming (Steel + Stagehand stack)
  //
  // BROWSER_SESSION_STARTED is sandbox→server: emitted the first time the agent
  // opens a Stagehand/Steel session inside the sandbox. The server mints a
  // Blaxel cast preview lazily on receipt and fans out BROWSER_STREAM_READY to
  // the user's frontend sockets. This keeps the canvas dormant until the agent
  // actually needs a browser — we no longer mint on every sandbox-ready.
  //
  // BROWSER_STREAM_READY/STOPPED are server→frontend.
  // ==========================================================================
  BROWSER_SESSION_STARTED: 'browser:session_started',
  // Sandbox → server: emitted by closeStagehandSession() so the server can
  // tear down the cached cast preview and fan BROWSER_STREAM_STOPPED out to
  // frontends. Fired both when the user manually closes the browser and when
  // the sandbox shuts down the Stagehand session for any other reason.
  BROWSER_SESSION_STOPPED: 'browser:session_stopped',
  BROWSER_STREAM_READY: 'browser:stream_ready',
  BROWSER_STREAM_STOPPED: 'browser:stream_stopped',

  // BROWSER_VIEW_MACHINE_V1: server-owned view snapshot (server→frontend) and
  // the explicit Wake demand (frontend→server). Legacy BROWSER_STREAM_* events
  // keep flowing while the flag is OFF; web ignores them when its flag is ON.
  BROWSER_VIEW_STATE: 'browser:view_state',
  // The PULL half of the same rail (frontend→server): "what is the view right
  // now?". BROWSER_VIEW_STATE is pushed once per transition, so a hidden tab, a
  // paused socket or a server redeploy can leave a pane wearing a snapshot of a
  // box that has since gone to sleep. The answer is always a fresh
  // BROWSER_VIEW_STATE to the asking socket. Idempotent, never wakes the box,
  // never bills — it is a question, not a gesture.
  BROWSER_VIEW_SYNC: 'browser:view_sync',
  BROWSER_WAKE: 'browser:wake',
  // Computer-level wake (desktop lock screen, BROWSER_WAKE{target:'computer'}):
  // server → sandbox "a human woke the MACHINE, not the browser". The sandbox
  // holds itself awake (same grant as a human browser wake) and does nothing
  // else — no Chrome, no session, no announce.
  COMPUTER_WAKE: 'computer:wake',
  // Computer-level sleep (desktop menubar power glyph, founder 2026-08-31):
  // frontend → server "the human put the computer to sleep" — the server
  // releases every viewer hold and forwards the same event server → sandbox,
  // where the runner drops its wake grant and takes the idle transition now.
  // The runner keeps its task guard: a turn in flight is never cut.
  COMPUTER_SLEEP: 'computer:sleep',
  // Sound from the computer (frontend → server ONLY): the web beats
  // `{ playing: true }` every few seconds while the person has the computer's
  // sound on and it is audible, and says `{ playing: false }` when it stops.
  // The server holds the box awake while the beat is fresh (relay/sound-hold.ts),
  // so music keeps playing when they look elsewhere in the app.
  COMPUTER_SOUND: 'computer:sound',
  // The agent's hands on the user's computer VIEW (founder 2026-08-31: "can
  // agent open and close computer by itself… open computer and go to this
  // app / open this preview"). Sandbox → server → frontend (forwarded): SHOW
  // opens the computer on a surface (or a named creation, full screen);
  // CLOSE closes the window. Pure view verbs — they never start, stop, or
  // wake anything, and they only fire live (never replayed), so an old task's
  // history can never pop the computer open.
  COMPUTER_SHOW: 'computer:show',
  COMPUTER_CLOSE: 'computer:close',

  // Full tab-list snapshot. Sandbox → server → frontend. Emitted whenever
  // the tab set changes (agent opens, user opens/closes, title/url change)
  // and on a 2s heartbeat to catch natural browser-driven changes.
  // Always a complete set, never a delta — so reconnecting frontends
  // rehydrate in one shot with zero diff-merge logic.
  BROWSER_TABS_UPDATED: 'browser:tabs_updated',

  // Embedded browser chrome tab strip (EMBEDDED_BROWSER_V1): sandbox → server
  // → frontend. A different channel and shape than BROWSER_TABS_UPDATED —
  // full snapshots whose tabs always carry `active` (the tab the runner's
  // session is BOUND to = what the pane shows and what every command drives)
  // plus a top-level taskId (the task owning the active tab, '' when none).
  // Emitted only by a local/embedded-lane runner; the neko lane never fires
  // it. Payload: BrowserTabsPayload.
  BROWSER_TABS: 'browser:tabs',

  // Frontend → server → sandbox: user clicked "+ new tab" or "close tab"
  // in the tab bar. The sandbox mutates Chrome via Stagehand's V3Context,
  // then fires a fresh BROWSER_TABS_UPDATED so all clients converge.
  BROWSER_OPEN_TAB: 'browser:open_tab',
  BROWSER_CLOSE_TAB: 'browser:close_tab',

  // Frontend → server → sandbox: user toggled the manual browser button in
  // the control pane. OPEN_SESSION lazily boots Stagehand/Steel (same path as
  // a browser_* tool call); CLOSE_SESSION tears it down. Server also
  // optimistically resets its browser-cast cache on CLOSE so the next open
  // mints a fresh preview URL.
  BROWSER_OPEN_SESSION: 'browser:open_session',
  BROWSER_CLOSE_SESSION: 'browser:close_session',
  /**
   * Frontend → server. Emitted every 5s while WebRTC is in 'streaming' state.
   * Payload: BrowserStatsPayload. Used by /api/admin/browser-stats/:userId
   * for live observability of stream health (FPS, packet loss, RTT, bitrate).
   * Phase 1 of streaming quality work — see specs/2026-04-29-neko-streaming-world-class-design.md.
   */
  BROWSER_STATS: 'browser:stats',
  // Frontend → server → sandbox: heartbeat when the user is actively
  // interacting with the browser pane (mouse move, click, keypress, scroll).
  // Sandbox calls activityManager.touch() on receipt so the 24s inactivity
  // timer resets — prevents the browser from auto-closing while the user
  // is still using it (e.g., typing a Gmail password over 30s). Frontend
  // throttles to ~1 emit per second to avoid flood.
  BROWSER_USER_INPUT: 'browser:user_input',

  // Frontend → server: the pane's tab went hidden past its grace, or came back
  // (work holds the box, spec §5.1). A pane's wires are INBOUND, so a tab left
  // hidden holds a paid container awake with nobody looking. `visible: false`
  // drops the viewer hold at once instead of waiting out its sixty-second cap.
  // Web → server ONLY: nothing forwards it to the sandbox or back down.
  BROWSER_PANE_VISIBILITY: 'browser:pane_visibility',

  // Frontend → server → sandbox: the user clicked a navigation control in the
  // embedded browser pane's chrome (back / forward / reload / address-bar goto /
  // tab switch / new tab). The runner maps the action onto CDP against the
  // ATTACHED local/embedded browser only (never neko/extension, never a cold
  // boot), and user navigation obeys the SAME nav/egress guards as agent
  // navigation. Payload: BrowserNavigatePayload.
  BROWSER_NAVIGATE: 'browser:navigate',

  // Local in-app browser (LOCAL_BROWSER_V1): sandbox → server → frontend.
  // READY carries the loopback view-server coordinates (wsUrl + token) — the
  // data plane (frames/input) is ws://127.0.0.1 and never crosses the network;
  // only this announcement rides the relay. Emitted by a dial-mode runner when
  // a real consumer opens the local browser. STOPPED mirrors
  // BROWSER_SESSION_STOPPED semantics for the local lane.
  BROWSER_LOCAL_READY: 'browser:local_ready',
  BROWSER_LOCAL_STOPPED: 'browser:local_stopped',

  // Frontend → server ONLY: the live view's beat, every ~5 s while its Neko
  // client is connected and the tab is on screen (apps/web
  // hooks/useBrowserViewingBeat.ts). Presence: the server's awake guard never
  // restarts Neko under someone watching (relay/browser-viewing.ts). It holds,
  // forwards and wakes nothing. (The name outlived the P2 viewer lease, which
  // sent it before that lease was deleted.)
  BROWSER_VIEWING: 'browser:viewing',
  // P2 presence: server → frontend. The viewer lease lapsed (idle heartbeat
  // gap or socket disconnect) and the server suspended the stream. Payload:
  // BrowserStreamSuspendedPayload. Never emitted by the sandbox.
  BROWSER_STREAM_SUSPENDED: 'browser:stream_suspended',
  // P2 presence: server → frontend. A viewer returned and the server resumed
  // the stream. Payload: BrowserStreamResumedPayload. Never emitted by the
  // sandbox.
  BROWSER_STREAM_RESUMED: 'browser:stream_resumed',
  // P3 shared control ([ow1] cobrowse telemetry): sandbox → server. The
  // shared-control guard (computer-use-tools.ts userControlGuard) refused an
  // agent action because the human was still driving at the wait cap — the
  // refusal itself is a plain isError ToolResult, invisible as a structured
  // signal server-side, so the guard forwards this minimal event. Payload:
  // { taskId }. Server-consumed only (cobrowse-telemetry.ts emit); deliberately
  // NOT in FORWARD_EVENTS — it never travels to the frontend.
  BROWSER_CONTROL_DEFERRED: 'browser:control_deferred',

  // BROWSER_MIRROR_V1 (the mirror method): sandbox → server. A batch of rrweb
  // recorder events captured from the in-sandbox browser and shipped over the
  // reverse-WS relay. NOT in FORWARD_EVENTS — the server owns a dedicated
  // buffered fan-out handler (a ring buffer of the current segment that replays
  // to a late-joining/refreshing panel), exactly like BROWSER_VIEWING; the
  // generic relay forward would flood and lose the bootstrap-on-join replay.
  // Payload: BrowserMirrorEventPayload { sessionId, seq (monotonic per recorder),
  // events (raw rrweb JSON strings) }.
  BROWSER_MIRROR_EVENT: 'browser:mirror_event',

  // BROWSER_MIRROR_V1: server → sandbox. Ask the in-sandbox recorder to emit a
  // fresh rrweb full snapshot (rrwebRecord.takeFullSnapshot) so a late-joining or
  // buffer-stale/gapped panel gets a coherent segment to bootstrap from. The
  // sandbox handler is a no-op when the recorder is not armed (flag off / no
  // browser). Server debounces (≥5s per user). Payload:
  // BrowserMirrorResnapshotPayload { sessionId }.
  BROWSER_MIRROR_RESNAPSHOT: 'browser:mirror_resnapshot',

  // Browser fetch (POST /v1/users/{user_id}/browser/fetch): server → runner, open
  // one page in a background tab of the box's own browser, with no model, and keep
  // the bodies of the page's own data calls; runner → server, the one answer,
  // echoing the requestId (awaitSandboxEvent). Payloads: BrowserFetchRequestPayload /
  // BrowserFetchResultPayload. Server ↔ runner ONLY, never forwarded.
  BROWSER_FETCH_REQUEST: 'browser:fetch_request',
  BROWSER_FETCH_RESULT: 'browser:fetch_result',

  // Browser cookies (GET /v1/users/{user_id}/browser/cookies): server → runner, which cookies of
  // some names the box browser holds for one allowed site, and until when; runner → server, the
  // one answer, echoing the requestId (awaitSandboxEvent). Names, sites and expiry only, never a
  // value. Payloads: BrowserCookiesRequestPayload / BrowserCookiesResultPayload. Server ↔ runner
  // ONLY, never forwarded.
  BROWSER_COOKIES_REQUEST: 'browser:cookies_request',
  BROWSER_COOKIES_RESULT: 'browser:cookies_result',

  // The box door (box hosts spec §6): server → runner, read, write, list, stat, make,
  // remove or copy a path in the runner's own workspace, run one bounded command there, or
  // read the runner's last log lines, for a host whose runner dials out (boat, the person's
  // own computer); runner → server, the one answer, echoing the requestId
  // (awaitSandboxEvent). Payloads: BoxFsRequestPayload / BoxFsResultPayload,
  // BoxExecRequestPayload / BoxExecResultPayload, BoxLogsRequestPayload /
  // BoxLogsResultPayload. Server ↔ runner ONLY, never forwarded.
  BOX_FS_REQUEST: 'box:fs_request',
  BOX_FS_RESULT: 'box:fs_result',
  BOX_EXEC_REQUEST: 'box:exec_request',
  BOX_EXEC_RESULT: 'box:exec_result',
  BOX_LOGS_REQUEST: 'box:logs_request',
  BOX_LOGS_RESULT: 'box:logs_result',

  // Browser watch (GET /v1/users/{user_id}/browser/watch): server → runner, start (or
  // renew, a lease) and stop a read-only live view of the browser fetch's own tabs;
  // runner → server, each tab opened, picture, tab hidden and tab closed. Payloads:
  // BrowserWatchStartPayload / BrowserWatchStopPayload / BrowserWatchEventPayload.
  // Server ↔ runner ONLY, never forwarded.
  BROWSER_WATCH_START: 'browser:watch_start',
  BROWSER_WATCH_STOP: 'browser:watch_stop',
  BROWSER_WATCH_EVENT: 'browser:watch_event',

  // ==========================================================================
  // OS hands (macOS actuation lane) — ambient "hands active" signal
  // ==========================================================================
  // Sandbox → server → frontend: the OS-hands lane is (or stopped) operating the
  // user's Mac — a distinct app-actuation window derived from the os_* tool lane
  // and the sidecar takeover-state overlay. Ephemeral live-activity (like
  // AGENT_STATUS): NOT buffered, self-clears on reload. Payload:
  // OsHandsActivePayload. Feeds the web footer + notch so a takeover (real
  // cursor/keyboard held) is always visible. MUST be in FORWARD_EVENTS below.
  OS_HANDS_ACTIVE: 'os_hands:active',

  // Composer Eyes (Slice 1) — composer-direct OS perception RPC. Server →
  // brain (local runner): run ONE read-only AX perception op (apps/snapshot/
  // find) against the VestraHands sidecar; brain → server: the result echoing
  // the same requestId (server correlates via awaitSandboxEvent). Payloads:
  // OsPerceiveRequest / OsPerceiveResult. Server ↔ brain ONLY — deliberately
  // NOT in FORWARD_EVENTS; the frontend never sees these frames.
  OS_PERCEIVE_REQUEST: 'os:perceive:request',
  OS_PERCEIVE_RESULT: 'os:perceive:result',

  // Tier-0 verbs (OS_VERBS_V1) — Composer run_verb RPC. Server → brain (local
  // runner): run ONE registry verb (id + JSON params) through the VestraHands
  // sidecar's VerbEngine; brain → server: the outcome echoing the same
  // requestId (server correlates via awaitSandboxEvent). Payloads:
  // OsVerbRequest / OsVerbResult (computer-use/os-hands-protocol.ts, shipped
  // by the P0 wire task). Server ↔ brain ONLY — deliberately NOT in
  // FORWARD_EVENTS; the frontend never sees these frames.
  OS_VERB_REQUEST: 'os:verb:request',
  OS_VERB_RESULT: 'os:verb:result',

  // Dictate (user-directed app actuation) — the founder speaks a reply and
  // Vestra types it into an eyes-resolved app. Server → brain (local
  // runner): type user-authored text into the target app + optionally press
  // Return; brain → server: the outcome echoing the same requestId (server
  // correlates via awaitSandboxEvent). Payloads: OsDictateRequest /
  // OsDictateResult (computer-use/os-hands-protocol.ts). Server ↔ brain ONLY —
  // deliberately NOT in FORWARD_EVENTS; the frontend never sees these frames.
  OS_DICTATE_REQUEST: 'os:dictate:request',
  OS_DICTATE_RESULT: 'os:dictate:result',

  // Interact (user-directed app CONTROL) — the founder speaks a control ask
  // ("play it", "scroll down", "go back", "undo") and Vestra performs the
  // table-locked action on the eyes-resolved app. Server → brain (local
  // runner): run one key/scroll action on the target app; brain → server: the
  // outcome echoing the same requestId (server correlates via
  // awaitSandboxEvent). Payloads: OsInteractRequest / OsInteractResult
  // (computer-use/os-hands-protocol.ts). Server ↔ brain ONLY — deliberately
  // NOT in FORWARD_EVENTS; the frontend never sees these frames.
  OS_INTERACT_REQUEST: 'os:interact:request',
  OS_INTERACT_RESULT: 'os:interact:result',

  // Capture (composer context handoff) — the composer pulls ONE screenshot of
  // the gaze-target app to attach to a task it spawns (context transfer, not
  // mediation). Server → brain (local runner): capture the target app
  // (frontmost when no bundleId) through the sidecar's `screenshot` op; brain →
  // server: the outcome echoing the same requestId (server correlates via
  // awaitSandboxEvent). Payloads: OsCaptureRequest / OsCaptureResult
  // (computer-use/os-hands-protocol.ts). Server ↔ brain ONLY — deliberately
  // NOT in FORWARD_EVENTS; the frontend never sees these frames.
  OS_CAPTURE_REQUEST: 'os:capture:request',
  OS_CAPTURE_RESULT: 'os:capture:result',

  // The step lane for the composer (T8d, work_in_app): the server asks the
  // local runner to run the Jev step lane inside one Mac app for a composer
  // turn, with no task. REQUEST/RESULT are requestId-correlated (the server
  // awaits via awaitSandboxEvent); PROGRESS streams one line per act keyed by
  // laneId (never a requestId, or it would resolve the waiter); CANCEL stops
  // the stretch when the turn ends. Payloads: OsLaneRequest / OsLaneProgress /
  // OsLaneCancel / OsLaneResult (computer-use/os-lane-protocol.ts). Server ↔
  // brain ONLY — deliberately NOT in FORWARD_EVENTS.
  OS_LANE_REQUEST: 'os:lane:request',
  OS_LANE_PROGRESS: 'os:lane:progress',
  OS_LANE_CANCEL: 'os:lane:cancel',
  OS_LANE_RESULT: 'os:lane:result',

  // App recipes for the composer (T9, run_recipe): the server asks the local runner to walk
  // ONE recipe of OS_RECIPES (computer-use/os-recipes.ts) on the Mac in one dial. REQUEST/
  // RESULT are requestId-correlated (the server awaits via awaitSandboxEvent); CANCEL stops
  // the recipe before its next step when the turn ends. Payloads: OsRecipeRequest /
  // OsRecipeCancel / OsRecipeResult (computer-use/os-recipe-protocol.ts). Server ↔ brain
  // ONLY — deliberately NOT in FORWARD_EVENTS.
  OS_RECIPE_REQUEST: 'os:recipe:request',
  OS_RECIPE_CANCEL: 'os:recipe:cancel',
  OS_RECIPE_RESULT: 'os:recipe:result',

  // Installed-app inventory (Tier-0 verbs, PR 1.5) — brain → server PUSH, no
  // requestId: the brain re-asserts which registry apps exist on its Mac (on
  // connect + a slow beat); the server caches it per user and search_verbs
  // filters catalog hits through it (hint, never gate — a missing or stale
  // frame filters nothing). Payload: OsAppInventoryFrame
  // (computer-use/os-hands-protocol.ts). Server ↔ brain ONLY — deliberately
  // NOT in FORWARD_EVENTS; the frontend never sees this frame.
  OS_APP_INVENTORY: 'os:app-inventory',

  // ==========================================================================
  // Terminal (frontend ↔ server)
  //
  // V1: line-based exec into the user's sandbox. One round trip per command.
  // See docs/superpowers/specs/2026-05-26-terminal-tab-design.md
  // ==========================================================================

  /** frontend → server: run a shell command in the user sandbox. */
  TERMINAL_EXEC: 'terminal:exec',
  /** server → frontend: result of a previously emitted TERMINAL_EXEC. */
  TERMINAL_RESULT: 'terminal:result',

  // ==========================================================================
  // Vendor-engine sign-in (sandbox ↔ server)
  //
  // The box is the only place that knows whether its engine config dir is
  // signed in, so it reports upward and the server persists the answer.
  // ==========================================================================

  /**
   * Vendor-engine sign-in state (Claude Code). Sandbox → server, where it is persisted on
   * user_preferences so Settings can show "Connected" WITHOUT waking the box. Never forwarded
   * to the frontend: the panel reads the stored value over HTTP.
   */
  ENGINE_AUTH_STATUS: 'engine:auth_status',
  /** Server → sandbox: Settings → Disconnect. The box signs its engine config dir out. */
  ENGINE_AUTH_LOGOUT: 'engine:auth_logout',
  /**
   * Agent wallet (spec 2026-10-15 §2). Server → runner: the box-wide payment freeze, sent on every change; the runner
   * also pulls GET /api/internal/purchases/freeze each time it greets a server. Never forwarded. Payload:
   * PurchaseFreezeFrame (wallet/frames.ts PurchaseFreezePayloadSchema).
   */
  PURCHASE_FREEZE: 'purchase:freeze',
  /** Agent wallet: server → web (emitToUser), a shop's UPI app link to open on the person's phone. Never relay-forwarded. */
  PURCHASE_UPI_LINK: 'purchase:upi_link',
  /**
   * Sandbox → server: where the person stands on their own plan, read by the box from the
   * engine's own stream (engines spec §6.8), or pulled from the engine on request (T2b).
   * Persisted to engine_accounts; never forwarded. Payload: { taskId?, standing: EngineStanding }.
   */
  ENGINE_STANDING: 'engine:standing',
  /**
   * Server → sandbox: read the plan's standing now, without a turn (engines T2b). Only ever sent
   * to a box that is already awake (Settings' refresh never wakes one). Payload: { engine }.
   */
  ENGINE_STANDING_REFRESH: 'engine:standing_refresh',
  /**
   * Server → sandbox: Settings → Connect (engines T2a). Join the box's one sign-in flight as a
   * watcher with no task and report each phase as ENGINE_CONNECT_PROGRESS under this connectId.
   * Payload: { engine, connectId }.
   */
  ENGINE_CONNECT: 'engine:connect',
  /** Server → sandbox: the person left mid-connect. End THIS connect; a task waiting on the same
   *  sign-in keeps waiting. Payload: { engine, connectId }. */
  ENGINE_CONNECT_CANCEL: 'engine:connect_cancel',
  /**
   * Box → server, and server → the person's web sockets: one phase of a Settings connect.
   * Never on the generic forward path (it stamps a taskId and a connect belongs to no task):
   * the server re-emits the box's frames, and its own (waking, browser, failed), to the person.
   * Payload: { engine, connectId, phase, account?, reason? }.
   */
  ENGINE_CONNECT_PROGRESS: 'engine:connect_progress',
  /**
   * Server → sandbox: the person's live account slots (several accounts, spec 2026-10-01), sent
   * at every AGENT_READY of a runner that declares 'claude_code_slots'. Level-triggered: the box
   * deletes any slot dir not in the list (never 'primary', never one a turn is running on), so an
   * account removed while the box slept leaves no sign-in behind. Payload: { engine, slotIds }.
   */
  ENGINE_SLOTS_SYNC: 'engine:slots_sync',
} as const;

// ============================================================================
// Runner drain exit code (Bedrock Slice 5, Phase 6)
// ============================================================================
/**
 * The process exit code a LOCAL runner uses when it self-exits in response to an
 * EVENTS.RUNNER_DRAIN signal (finish turn → record head → release lease → exit).
 * Distinct from a crash (non-zero) and a plain graceful stop (0) so the CLI
 * supervisor can classify it: a drain-exit is a CLEAN self-update restart (no
 * backoff), a crash accrues backoff. 75 == BSD sysexits EX_TEMPFAIL ("temporary
 * failure; retry"), which is exactly the intent — come back on the new version.
 *
 * The CLI (apps/runner-cli) mirrors this VALUE locally (it is a standalone package
 * with no @bash/shared dependency); the two MUST stay in sync — see
 * apps/runner-cli/src/supervise.ts.
 */
export const RUNNER_DRAIN_EXIT_CODE = 75;

/**
 * Pull-based upgrades (RUNNER_PULL_V1, Layer 1 of the staleness program). A Blaxel
 * runner that has PRE-STAGED the intended bundle into the volume CAS
 * (/app/workspace/.cas/active/agent-runner.js, sha256-verified against the channel's
 * intended release) exits with THIS code at a process-wide turn boundary; the
 * run-agent wait-loop (apps/sandbox/bin/run-agent) relaunches node on the swapped
 * bundle in place — no server involvement, no PUT, no tracker.
 *
 * Distinct from RUNNER_DRAIN_EXIT_CODE (75) on purpose: 75 means "the SERVER lane
 * restarts me" (local CLI supervisor / Blaxel wake escalation — the exiting process
 * did NOT stage its successor's code); 76 means "my in-sandbox supervisor loop
 * relaunches me on the bundle I already staged". run-agent restarts ONLY on 76 —
 * a 75 still falls through to the server lane, so env/image drift (which an
 * in-place relaunch cannot fix — it inherits the spawn env) keeps today's path.
 */
export const RUNNER_PULL_RESTART_EXIT_CODE = 76;

/**
 * Box hosts: a runner on a cloud machine that dials out (boat) refused its acquire, because the workspace head
 * could not be moved in (never a fork: apps/sandbox services/runner-lease.ts), and exits with THIS code to be
 * tried again. Distinct from a crash so the launcher can count refusals: REFUSED_GIVE_UP_COUNT in a row and it
 * gives up, so the machine can be switched off (apps/runner-cli supervise.ts mirrors this VALUE by hand; user
 * 1bf3162d, 2026-10-19: a head whose object was gone held a boat machine on through 38 restarts). The Mac and
 * Blaxel never exit with it.
 */
export const RUNNER_REFUSED_EXIT_CODE = 77;

// ============================================================================
// Capability event re-export
// ============================================================================
// Thin alias for ergonomic imports — points at the single source of truth in EVENTS.
export const USER_CAPABILITIES = EVENTS.USER_CAPABILITIES;

/**
 * Type for event names
 */
export type EventName = (typeof EVENTS)[keyof typeof EVENTS];

/**
 * Events that are forwarded from agent to frontend (through server)
 */
export const FORWARD_EVENTS: EventName[] = [
  EVENTS.AGENT_START,
  EVENTS.AGENT_END,
  EVENTS.SESSION_STATE_CHANGED,
  EVENTS.AGENT_ERROR,
  EVENTS.AGENT_RESULT,
  EVENTS.AGENT_USAGE, // Token usage stats
  EVENTS.AGENT_CONTEXT_USAGE, // Periodic context window snapshot
  EVENTS.AGENT_MEMORY_RECALL, // SDK 0.3.144: memory files surfaced this turn
  EVENTS.AGENT_THINKING,
  EVENTS.AGENT_THINKING_DELTA,
  EVENTS.AGENT_THINKING_START,
  EVENTS.AGENT_TEXT,
  EVENTS.AGENT_TEXT_DELTA,
  EVENTS.AGENT_TOOL_USE,
  EVENTS.AGENT_TOOL_RESULT,
  // NOTE: EVENTS.AGENT_NESTED_TOOL_CALL is intentionally NOT forwarded. The relay
  // records it to the trace store directly (trace-only side channel), independent
  // of FORWARD_EVENTS, so forwarding it to the frontend would be dead traffic.
  EVENTS.AGENT_TODO,
  // SDK-native staging events
  EVENTS.MESSAGE_STAGED,
  EVENTS.MESSAGE_PICKED_UP,
  EVENTS.STAGING_SYNC, // K6 P5: staged-queue snapshot response (agent → frontend)
  // Sandbox state
  EVENTS.SANDBOX_STATE,
  // Phase 11: State Management events
  EVENTS.MESSAGE_SYNC,
  // Connection State events (Unified Connection Service)
  // Task events (Single ID refactor)
  EVENTS.TASK_CREATED,
  EVENTS.TASK_RESUMED,
  EVENTS.TASK_UPDATE,
  EVENTS.TASK_OUTPUT_REGISTERED,
  EVENTS.TASK_ARTIFACT_REGISTERED,
  EVENTS.TASK_PREVIEW_READY,
  EVENTS.TASK_PREVIEW_CLOSED,
  EVENTS.APPS_CHANGED,
  // The agent's computer-view verbs (sandbox → frontend, live only).
  EVENTS.COMPUTER_SHOW,
  EVENTS.COMPUTER_CLOSE,
  // Integration Auth / permission prompts / questions are surfaced via INTERACTION_REQUIRED.
  // K3-T6: it is forwarded by forwardToFrontend today but was ABSENT from this allowlist;
  // the isForwardAllowed() guard would silently drop every interaction without this entry.
  EVENTS.INTERACTION_REQUIRED,
  // State Robustness
  // Skill Ecosystem
  // Agent Ecosystem
  EVENTS.AGENT_CREATED,
  EVENTS.AGENT_UPDATED,
  EVENTS.AGENT_DELETED,
  // Agent Library
  EVENTS.AGENT_INSTALLED,
  EVENTS.AGENT_UNINSTALLED,
  EVENTS.AGENT_PUBLISHED,
  EVENTS.AGENT_UNPUBLISHED,
  // Subagent streaming
  EVENTS.SUBAGENT_START,
  EVENTS.SUBAGENT_TEXT_DELTA,
  EVENTS.SUBAGENT_TOOL_USE,
  EVENTS.SUBAGENT_TOOL_RESULT,
  EVENTS.SUBAGENT_END,
  EVENTS.SUBAGENT_SPAWNED,
  EVENTS.SUBAGENT_MESSAGE,
  // Streaming enrichment events
  EVENTS.AGENT_TOOL_USE_START,
  EVENTS.AGENT_TOOL_PROGRESS,
  EVENTS.AGENT_STATUS,
  // Workspace Sync
  EVENTS.WORKSPACE_SYNC_STARTED,
  EVENTS.WORKSPACE_SYNC_COMPLETED,
  // User-file events
  EVENTS.USER_FILE_SYNCED,
  EVENTS.USER_FILE_READY,
  // Credit events
  EVENTS.CREDIT_UPDATE,
  EVENTS.CREDIT_WARNING,
  EVENTS.CREDIT_EXHAUSTED,
  // Memory events
  // Routine events
  EVENTS.ROUTINE_CREATED,
  EVENTS.ROUTINE_UPDATED,
  EVENTS.ROUTINE_DELETED,
  EVENTS.ROUTINE_RUN_STARTED,
  EVENTS.ROUTINE_RUN_COMPLETED,
  EVENTS.ROUTINE_RUN_FAILED,
  EVENTS.ROUTINE_REGISTER_FAILED,
  EVENTS.ROUTINE_COMPLETED,
  // Prompt suggestions
  EVENTS.PROMPT_SUGGESTION,
  // Browser streaming (Steel + Stagehand)
  EVENTS.BROWSER_STREAM_READY,
  EVENTS.BROWSER_STREAM_STOPPED,
  EVENTS.BROWSER_TABS_UPDATED,
  // Embedded browser chrome tab strip (local/embedded lane only).
  EVENTS.BROWSER_TABS,
  // Local in-app browser (LOCAL_BROWSER_V1) — sandbox → server → frontend.
  EVENTS.BROWSER_LOCAL_READY,
  EVENTS.BROWSER_LOCAL_STOPPED,
  // K3-T6: events forwarded to frontend but previously absent from the allowlist.
  // Adding them here ensures isForwardAllowed() passes and forwardToFrontend()
  // does not silently drop them when the guard is active.
  // ROUTINE_CREATED_IN_TASK — sandbox emits after routine detection hook fires;
  // frontend renders the routine confirmation card in the task stream.
  EVENTS.ROUTINE_CREATED_IN_TASK,
  // ITEM_CREATED_IN_TASK — sandbox emits after a skill/agent/ability is created
  // in a task; frontend renders the item confirmation card in the task stream.
  EVENTS.ITEM_CREATED_IN_TASK,
  // CREDIT_APPROVAL_NEEDED — server emits via forwardToFrontend when an expensive
  // tool needs user approval; frontend renders an inline credit-approval block.
  EVENTS.CREDIT_APPROVAL_NEEDED,
  // SESSION_COMPACTED — sandbox PostCompact hook emits after SDK auto-summarizes
  // older turns to free context; frontend renders an inline "context summarized" card.
  EVENTS.SESSION_COMPACTED,
  // PERMISSION_DENIED (K6-P3-C7): the gate refused a tool call (native and Claude Code). The
  // relay composes the person's line onto it (`notice`); the web's noticeHandlers renders it.
  // SET_PERMISSION_MODE is intentionally ABSENT: it is a frontend→server→sandbox
  // CONTROL event, never forwarded sandbox→frontend.
  EVENTS.PERMISSION_DENIED,
  // OS_HANDS_ACTIVE — sandbox→web ambient signal that the OS-hands lane is
  // operating the user's Mac (esp. under a real cursor/keyboard takeover). Without
  // membership, isForwardAllowed() silently drops it and the footer/notch never
  // light up. Ephemeral (not buffered) — a pure forward-first pass-through.
  EVENTS.OS_HANDS_ACTIVE,
  // TASK_NOTICE (engines T0): one quiet line for the person in a task. The relay buffers it
  // (the turn persists it as a `notice` block) and forwards it to noticeHandlers.
  EVENTS.TASK_NOTICE,
];

/**
 * Content block events for streaming
 */
export const CONTENT_EVENTS: EventName[] = [
  EVENTS.AGENT_THINKING,
  EVENTS.AGENT_THINKING_DELTA,
  EVENTS.AGENT_TEXT,
  EVENTS.AGENT_TEXT_DELTA,
  EVENTS.AGENT_TOOL_USE,
  EVENTS.AGENT_TOOL_USE_START,
  EVENTS.AGENT_TOOL_RESULT,
];
